// The Reliquary (docs/04 §20, docs/05 §4): a dusty shelf of relic slots, bought with Teeth.
import { RELIC_LIST } from '../../content/registry';
import { t } from '../../i18n/t';
import { controller } from '../../render';
import { relicAvailable, relicSlotCost } from '../../sim/meta';
import { B } from '../../content/balance';
import { setUI, useGame } from '../../state/store';
import { Btn, Frame, Icon, KText } from '../common';
import { tip } from '../tooltip/Tooltip';

export function RelicTip({ id }: { id: string }) {
  return (
    <div className="col">
      <span className="h" style={{ color: '#eaaded' }}>
        {t(`relic.${id}.name`)}
      </span>
      <KText text={t(`relic.${id}.desc`)} />
    </div>
  );
}

export function Reliquary() {
  const meta = useGame((g) => g.meta);
  const r = meta.relics;
  const slotCost = relicSlotCost(meta);
  const firstFree = r.slotted.indexOf(null);
  return (
    <div className="overlay interactive" style={{ inset: '16px 0 12px 0', flexDirection: 'column', gap: 6, zIndex: 20 }}>
      <div className="row" style={{ width: 600 }}>
        <span className="h" style={{ color: '#eaaded' }}>
          {t('ui.reliquary.title')}
        </span>
        <span>
          <span className="kw-value">
            {t('ui.teeth')} {meta.teeth}
          </span>
          {'  '}
          <span style={{ color: '#a884f3' }}>{t('ui.reliquary.shards', { n: r.shards })}</span>
        </span>
        <Btn kind="ghost" onClick={() => setUI({ panel: null })}>
          ✕
        </Btn>
      </div>
      <div className="dim" style={{ width: 600 }}>
        {t('ui.reliquary.hint')}
      </div>
      {/* the shelf */}
      <div className="row" style={{ width: 600, justifyContent: 'center', gap: 8, padding: '6px 0 10px', background: '#4c3e24', boxShadow: '0 4px 0 #2e222f, inset 0 -3px 0 #676633' }}>
        {r.slotted.map((id, i) => (
          <div
            key={i}
            className="card interactive"
            style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: id ? 'pointer' : 'default' }}
            onClick={() => id && controller.act({ t: 'SLOT_RELIC', id: null, slot: i })}
            {...(id ? tip(<RelicTip id={id} />) : tip(<span>{t('ui.reliquary.empty')}</span>))}
            data-testid={`relic-slot-${i}`}
          >
            {id ? <Icon id={`relic_${id}`} /> : <span className="dim">{t('ui.reliquary.empty')}</span>}
          </div>
        ))}
        {slotCost !== null && (
          <Btn disabled={meta.teeth < slotCost} onClick={() => controller.act({ t: 'BUY_RELIC_SLOT' })}>
            {t('ui.reliquary.buySlot', { cost: slotCost })}
          </Btn>
        )}
      </div>
      <div className="scroll" style={{ width: 600, maxHeight: 190, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4 }}>
        {RELIC_LIST.map((def) => {
          const owned = r.owned.includes(def.id);
          const slotted = r.slotted.includes(def.id);
          const avail = relicAvailable(meta, def.id);
          const locked = !owned && !avail;
          return (
            <div key={def.id} className="card col" style={{ alignItems: 'center', gap: 2, padding: 3, opacity: slotted ? 0.5 : 1 }} {...(locked ? tip(<span>{t('ui.reliquary.locked')}</span>) : tip(<RelicTip id={def.id} />))}>
              <Icon id={def.sprite} style={locked ? { filter: 'brightness(0)' } : undefined} />
              <span style={{ textAlign: 'center', minHeight: 20 }}>{locked ? '???' : t(`relic.${def.id}.name`)}</span>
              {owned ? (
                <Btn
                  kind={slotted ? 'ghost' : 'primary'}
                  disabled={slotted}
                  onClick={() => controller.act({ t: 'SLOT_RELIC', id: def.id, slot: firstFree >= 0 ? firstFree : 0 })}
                >
                  {slotted ? t('ui.reliquary.owned') : t('ui.reliquary.slot')}
                </Btn>
              ) : locked ? null : (
                <Btn disabled={meta.teeth < def.cost} onClick={() => controller.act({ t: 'BUY_RELIC', id: def.id })} testId={`buy-relic-${def.id}`}>
                  {t('ui.reliquary.buy', { cost: def.cost })}
                </Btn>
              )}
            </div>
          );
        })}
      </div>
      <span className="dim">{t('ui.reliquary.shardHint', { n: B.SHARDS_PER_RELIC })}</span>
    </div>
  );
}

/** Molt (docs/04 §20): shed the Web's silver memories and your Souls for Teeth and a new skin. */
export function MoltButton() {
  const meta = useGame((g) => g.meta);
  if (!(meta.nodes.G_MOLT ?? 0)) return null;
  return (
    <div className="row" style={{ gap: 4 }}>
      <Btn kind="danger" disabled={meta.cycleMaxTribute < B.MOLT_MIN_TRIBUTE} onClick={() => setUI({ confirmMolt: true })} testId="molt">
        {meta.cycleMaxTribute < B.MOLT_MIN_TRIBUTE ? t('ui.molt.need', { n: B.MOLT_MIN_TRIBUTE }) : t('ui.molt.button', { teeth: moltTeeth(meta.cycleMaxTribute) })}
      </Btn>
      <Btn onClick={() => setUI({ panel: 'reliquary' })} testId="reliquary">
        {t('ui.reliquary.open')}
      </Btn>
    </div>
  );
}

function moltTeeth(max: number): number {
  const over = max - B.TEETH_OFFSET;
  return over > 0 ? Math.floor(Math.pow(over, B.TEETH_EXP)) : 0;
}

export function MoltConfirm() {
  const form = useGame((g) => g.meta.form);
  const next = B.FORMS[Math.min(B.FORMS.length - 1, B.FORMS.indexOf(form) + 1)]!;
  return (
    <div className="overlay interactive" style={{ zIndex: 30 }}>
      <Frame style={{ width: 280, textAlign: 'center' }}>
        <div className="col" style={{ gap: 8, padding: 6 }}>
          <div className="h kw-bad">{t('ui.molt.title')}</div>
          <div>{t('ui.molt.confirm')}</div>
          <KText text={`${t(`ui.form.${next}`)}: ${t(`ui.form.desc.${next}`)}`} />
          <div className="row" style={{ justifyContent: 'center', gap: 10 }}>
            <Btn onClick={() => setUI({ confirmMolt: false })}>{t('ui.molt.no')}</Btn>
            <Btn
              kind="danger"
              onClick={() => {
                setUI({ confirmMolt: false });
                controller.act({ t: 'MOLT' });
              }}
              testId="molt-confirm"
            >
              {t('ui.molt.yes')}
            </Btn>
          </div>
        </div>
      </Frame>
    </div>
  );
}
